import { useState, useEffect } from 'react'
import Navbar from '../components/layout/Navbar'
import api from '../api/client'

export default function WarmProspectsPage() {
  const [replies, setReplies] = useState([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)
  const [textModal, setTextModal] = useState(null)
  const [textMessage, setTextMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [filter, setFilter] = useState('all')

  useEffect(() => { loadReplies() }, [])

  const loadReplies = async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/sms/replies')
      setReplies(res.data)
    } catch(e) {
      setMsg({ type: 'error', text: 'Failed to load replies' })
    } finally {
      setLoading(false)
    }
  }

  const handleSendText = async () => {
    if (!textMessage.trim()) return
    setSending(true)
    try {
      await api.post('/api/sms/send-direct', {
        phone: textModal.phone,
        message: textMessage,
      })
      setMsg({ type: 'success', text: `Message sent to ${textModal.company}!` })
      setTextModal(null)
      setTextMessage('')
      loadReplies()
    } catch(e) {
      setMsg({ type: 'error', text: 'Failed to send message' })
    } finally {
      setSending(false)
    }
  }

  const filtered = replies.filter(r => {
    if (filter === 'all') return true
    return r.reply_type === filter
  })

  const replyTypeBadge = (type) => {
    const map = {
      interested: { background: '#EAF3DE', color: '#27500A' },
      stop: { background: '#FCEBEB', color: '#791F1F' },
      confused: { background: '#FEF3CD', color: '#7D5A00' },
      other: { background: '#F1EFE8', color: '#5F5E5A' },
    }
    return map[type] || map.other
  }

  const counts = {
    all: replies.length,
    interested: replies.filter(r => r.reply_type === 'interested').length,
    confused: replies.filter(r => r.reply_type === 'confused').length,
    other: replies.filter(r => r.reply_type === 'other').length,
    stop: replies.filter(r => r.reply_type === 'stop').length,
  }

  return (
    <div style={styles.root}>
      <Navbar />
      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.pageTitle}>🔥 Warm Prospects</h1>
            <p style={styles.pageSub}>Prospects who replied to your SMS campaigns — prioritize these for follow-up.</p>
          </div>
          <button style={styles.refreshBtn} onClick={loadReplies}>↻ Refresh</button>
        </div>

        {msg && (
          <div style={msg.type === 'success' ? styles.successBanner : styles.errorBanner}>
            {msg.text}
          </div>
        )}

        <div style={styles.filterRow}>
          {[
            { key: 'all', label: 'All replies' },
            { key: 'interested', label: '✅ Interested' },
            { key: 'confused', label: '❓ Confused' },
            { key: 'other', label: '💬 Other' },
            { key: 'stop', label: '🛑 Stopped' },
          ].map(({ key, label }) => (
            <button
              key={key}
              style={{ ...styles.filterBtn, ...(filter === key ? styles.filterBtnActive : {}) }}
              onClick={() => setFilter(key)}
            >
              {label} <span style={styles.filterCount}>{counts[key]}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div style={styles.empty}>Loading warm prospects...</div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyTitle}>No replies yet</p>
            <p style={styles.emptySub}>When prospects reply to your SMS campaigns they'll appear here.</p>
          </div>
        ) : (
          <div style={styles.cardList}>
            {filtered.map(r => (
              <div key={r.id} style={styles.card}>
                <div style={styles.cardTop}>
                  <div>
                    <div style={styles.company}>{r.company}</div>
                    <div style={styles.meta}>
                      {r.contact && <span>{r.contact} · </span>}
                      <span>{r.phone}</span>
                      {r.email && <span> · {r.email}</span>}
                    </div>
                    <div style={styles.replyBox}>
                      <span style={{ ...styles.badge, ...replyTypeBadge(r.reply_type) }}>{r.reply_type}</span>
                      <span style={styles.replyText}>"{r.reply_text}"</span>
                      <span style={styles.replyTime}>{r.reply_received_at ? new Date(r.reply_received_at).toLocaleString() : ''}</span>
                    </div>
                    {r.follow_up_sent && (
                      <div style={styles.followUpSent}>✅ Auto follow-up link sent</div>
                    )}
                    <div style={styles.campaignMeta}>Campaign: {r.campaign_name} · Score: {r.score}</div>
                  </div>
                  <div style={styles.scoreCircle}>{r.score}</div>
                </div>
                <div style={styles.cardActions}>
                  <a href={`tel:${r.phone}`} style={styles.callBtn}>
                    📞 Call
                  </a>
                  <button
                    style={styles.textBtn}
                    onClick={() => { setTextModal(r); setTextMessage('') }}
                  >
                    💬 Text
                  </button>
                  {r.email && (
                    <a href={`mailto:${r.email}`} style={styles.emailBtn}>
                      📧 Email
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {textModal && (
        <div style={styles.overlay} onClick={e => e.target === e.currentTarget && setTextModal(null)}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Text {textModal.company}</h2>
              <button style={styles.closeBtn} onClick={() => setTextModal(null)}>✕</button>
            </div>
            <div style={styles.modalMeta}>
              {textModal.phone} {textModal.contact && `· ${textModal.contact}`}
            </div>
            <div style={styles.replyContext}>
              <span style={styles.replyContextLabel}>Their reply:</span>
              <span>"{textModal.reply_text}"</span>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Your message
                <span style={{ float: 'right', color: textMessage.length > 160 ? '#E24B4A' : '#888' }}>
                  {textMessage.length}/160
                </span>
              </label>
              <textarea
                value={textMessage}
                onChange={e => setTextMessage(e.target.value)}
                placeholder="Type your personal message..."
                style={styles.textarea}
                autoFocus
              />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setTextModal(null)}>Cancel</button>
              <button
                style={styles.saveBtn}
                onClick={handleSendText}
                disabled={sending || !textMessage.trim()}
              >
                {sending ? 'Sending...' : 'Send text'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  root: { minHeight: '100vh', background: '#f5f5f3' },
  main: { maxWidth: 900, margin: '0 auto', padding: '1.5rem' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' },
  pageTitle: { fontSize: 22, fontWeight: 500, color: '#1a1a1a', marginBottom: 4 },
  pageSub: { fontSize: 14, color: '#888' },
  refreshBtn: { background: 'none', border: '0.5px solid #ccc', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: '#444' },
  successBanner: { background: '#EAF3DE', color: '#27500A', border: '0.5px solid #C0DD97', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: '1rem' },
  errorBanner: { background: '#FCEBEB', color: '#791F1F', border: '0.5px solid #F09595', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: '1rem' },
  filterRow: { display: 'flex', gap: 6, marginBottom: '1rem', flexWrap: 'wrap' },
  filterBtn: { background: '#fff', border: '0.5px solid #e0dfd8', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center', gap: 6 },
  filterBtnActive: { background: '#1a1a1a', color: '#fff', borderColor: '#1a1a1a' },
  filterCount: { background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '1px 6px', fontSize: 11 },
  cardList: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { background: '#fff', border: '0.5px solid #e0dfd8', borderRadius: 12, padding: '1.25rem' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' },
  company: { fontSize: 16, fontWeight: 500, color: '#1a1a1a', marginBottom: 4 },
  meta: { fontSize: 12, color: '#888', marginBottom: 8 },
  replyBox: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 },
  badge: { padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500 },
  replyText: { fontSize: 13, color: '#1a1a1a', fontStyle: 'italic' },
  replyTime: { fontSize: 11, color: '#aaa' },
  followUpSent: { fontSize: 12, color: '#27500A', marginBottom: 4 },
  campaignMeta: { fontSize: 12, color: '#aaa' },
  scoreCircle: { width: 44, height: 44, borderRadius: '50%', background: '#f5f5f3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#1a1a1a', flexShrink: 0 },
  cardActions: { display: 'flex', gap: 8, borderTop: '0.5px solid #e0dfd8', paddingTop: '1rem' },
  callBtn: { background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 },
  textBtn: { background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  emailBtn: { background: 'none', border: '0.5px solid #ccc', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: '#444', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 },
  emptyState: { textAlign: 'center', padding: '4rem 2rem' },
  emptyTitle: { fontSize: 16, fontWeight: 500, color: '#1a1a1a', marginBottom: 6 },
  emptySub: { fontSize: 14, color: '#888' },
  empty: { textAlign: 'center', padding: '2rem', color: '#888' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { background: '#fff', borderRadius: 16, border: '0.5px solid #e0dfd8', padding: '1.5rem', width: 480, maxWidth: '95vw' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  modalTitle: { fontSize: 16, fontWeight: 500, color: '#1a1a1a' },
  closeBtn: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#888' },
  modalMeta: { fontSize: 13, color: '#888', marginBottom: 12 },
  replyContext: { background: '#f5f5f3', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#444', marginBottom: 12, display: 'flex', gap: 8 },
  replyContextLabel: { color: '#888', whiteSpace: 'nowrap' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
  label: { fontSize: 11, color: '#666' },
  textarea: { resize: 'vertical', minHeight: 100, padding: 8, fontFamily: 'inherit', fontSize: 13, border: '0.5px solid #ccc', borderRadius: 8, outline: 'none', width: '100%', boxSizing: 'border-box' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  cancelBtn: { background: 'none', border: '0.5px solid #ccc', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#444' },
  saveBtn: { background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
}